import React from "react";
import {
  MdOutlineThumbDownAlt,
  MdOutlineThumbUpAlt,
  MdThumbDownAlt,
  MdThumbUpAlt
} from "react-icons/md";
import { LLMFeedbackType } from "@blisspointmedia/bpm-types/dist/LLM";

const FEEDBACK_ICONS: Record<LLMFeedbackType, Record<LLMIconState, React.ComponentType>> = {
  NEGATIVE: {
    active: MdThumbDownAlt,
    inactive: MdOutlineThumbDownAlt,
  },
  POSITIVE: {
    active: MdThumbUpAlt,
    inactive: MdOutlineThumbUpAlt,
  },
};

type LLMIconState = "active" | "inactive";
const getIconState = (isActive: boolean): LLMIconState => (isActive ? "active" : "inactive");
export const getFeedbackIcon = (
  feedbackType: LLMFeedbackType,
  isActive: boolean
): React.ReactElement => {
  const IconComponent = FEEDBACK_ICONS[feedbackType][getIconState(isActive)];
  return React.createElement(IconComponent);
};

export const NEGATIVE_FEEDBACK_OPTIONS = [
  "The data is wrong or incomplete.",
  "The answer doesn't address my specific question.",
];

const SHORT_PROMPTS = [
  "What was total spend by platform last month?",
  "What was ROAS last quarter? Use platform data",
  "How many orders did we get last week? Use platform data",
  "What was the CPX in July 2025? Use platform data",
  "How many clicks did we get by platform last year?",
  "What was the CPM by channel for the last 30 days?",
  "How much revenue did we generate by surface last month? Use platform data",
  "What is our CTR MTD? ",
  "How many sessions did we get last week? Use platform data",
  "How many impressions did we serve last month by platform?",
  "What was CPC last month?",
  "How much did we spend on each channel in the last 30 days?",
  "What was our total order volume last year? Use platform data",
  "What was the CTR by campaign last week?",
  "What is our paid shopping and paid social ROAS YTD? Use platform data",
  "How many impressions did we serve on Facebook, Tiktok, and YouTube last week?",
  "What was our paid search and display CPX last month? Use platform data",
  "What was our display and social revenue last quarter? Use platform data",
  "What was the ROAS for Google and Meta campaigns for the last 90 days? Use platform data",
  "What was our WoW change in spend?",
  "How much did our order volume change by platform WoW? Use platform data",
  "Did our CPM increase or decrease WoW by channel, and by how much?",
  "What was our MoM change in spend?",
  "How much did CPX change on Google and Meta last month vs month prior?  Use platform data",
  "What was our MoM % change in revenue by surface? Use platform data",
  "What was our search CPC change WoW?",
  "How does CPX compare to this year vs last year? Use platform data",
  "How much did our order volume change for commerce and search WoW? Use platform data",
  "What was the MoM percentage change in CPM by channel?",
  "Did our clicks and impressions conversion rate change MoM? Use platform data",
  "How much did paid social and paid search revenue increase or decrease WoW? Use platform data",
  "How much did CPX change WoW? Use platform data",
  "What was the MoM change in paid search and paid social clicks & impressions?",
  "How much did our spend & CPC change last week vs the week before?",
  "Compare CPM by surface for the last 30 days vs the 30 days prior?",
  "What was the MoM percentage change in CPX by platform? Use platform data",
  "What was the MoM change in Facebook and Google clicks?",
  "Which platform had the largest WoW increase in spend?",
  "What campaign had the largest percentage increase in KPI volume last week? Use platform data",
  "What platform had the largest WoW change in CPM?",
  "Which channel had the largest positive change in spend WoW?",
  "Which platform had the biggest change in CPC WoW?",
  "Which channel had the lowest CPC/cost per conversion last week? Use platform data",
  "What platform had the highest WoW percentage increase in CPX? Use platform data",
  "Which channel had the biggest change in ROAS MoM? Use platform data",
  "Which campaign had the largest percentage change in Clicks MoM?",
  "Which tactic had the highest KPI Volume last month? Use platform data",
  "Which surface had the largest MoM increase in revenue?",
  "Which surface had the biggest drop in spend last month?",
  "Which campaign ID was the biggest driver of the MoM increase in spend?",
  "What platform had the largest decrease in CPC MoM?",
  "What was the best performing platform in terms of ROAS last quarter? Use platform data",
  "Which channel had the largest percentage change in Impressions last quarter?",
  "Which channel had the largest positive change in revenue QoQ? Use platform data",
  "What was spend yesterday?",
];

// Prompts we don't want to suggest on the New Chat cards because they're too long,
// but do want to include in the dropdown list of suggestions
const LONG_PROMPTS = [
  "How much did ROAS change last month vs month prior on paid social and paid search?",
  "How did our total spend change between last quarter and the quarter prior broken down by channel and platform?",
  "Which channel had the most significant percentage increase in conversions last month? Use platform data",
];

export const getPromptLibrary = (includeLong = false): { label: string; value: string }[] => {
  const promptsToUse: string[] = includeLong ? [...SHORT_PROMPTS, ...LONG_PROMPTS] : SHORT_PROMPTS;

  return promptsToUse.map(prompt => {
    const suffix = "Use platform data";

    let label = prompt;
    if (label.endsWith(suffix)) {
      label = label.slice(0, prompt.length - suffix.length);
    }

    return {
      label,
      value: prompt,
    };
  });
};

export const PRIVACY_POLICY_URL = "https://tinuiti.com/privacy-policy/";

export const ACKNOWLEDGEMENT_PHRASES = [
  "Fetching the necessary data for your request now!",
  "On it! Actively pulling the required data.",
  "Retrieving those data points. Stand by for the results!",
  "I'm searching high and low for that precise data point.",
  "Zipping through the database to find what you need.",
  "We are on the trail, collecting the exact data you requested.",
  "Compiling the data set now. Thanks for your patience!",
  "Running the query. Data is on the way!",
  "Hold tight! I'm initiating the data lookup.",
  "Looking up the requested data. Almost ready!",
  "Sourcing the data you asked for. Thanks for waiting!",
  "Just a moment—data retrieval is in progress!",
  "Focusing on the data pull. I'll be right back with the answer!",
  "Loading the data channels... the process is underway!",
  "Data retrieval in progress!",
  "Looking for your data!",
  "Analyzing the request and fetching data!",
];
